/**
 * feat-107 Slice A — left-rail Master tree (Lessons → Drills).
 *
 * Persistent wayfinding: a search box, collapsible Lessons, and their child
 * Drills. Drag handles (⠿) reorder rows in-place (HTML5 DnD) — this REPLACES the
 * old Sort number field (declutter win #2). Selecting a Drill drives the center
 * + right panes. All level words come from QUIZ_TERMS (never a hard-coded
 * "Topic"/"Drill"); titles truncate here (long-string state).
 */

import React, { useEffect, useRef, useState } from 'react';
import type { QuizUnit, QuizLesson } from '../../../services/quizContentAdminService';
import { lessonImagePublicUrl } from '../../../services/quizContentAdminService';
import { QUIZ_TERMS } from '../quizTerminology';
import { formatReleaseInstant, isScheduledLater } from './lessonSchedule';
import { LessonApprovalChip } from './approval/LessonApprovalChip';
import { SKIP_DRIP_LABEL } from '../drip/dripCopy';
import { CopyGlyph } from './CopyGlyph';
import { COPY_CHIP_LABEL, COPY_OF_DELETED, copyOfInfo, copyOfTitle } from './lessonCopyOfCopy';
import type { LessonApprovalStatus } from '../../../services/curriculumApprovalService';

export interface MasterDetailTreeProps {
  units: QuizUnit[];
  search: string;
  onSearch: (q: string) => void;
  expandedUnitIds: ReadonlySet<string>;
  onToggleUnit: (unitId: string) => void;
  selectedDrillId: string | null;
  onSelectDrill: (unitId: string, drill: QuizLesson) => void;
  glyphForKey: (key: string) => string;
  onCreateLesson: (title: string) => Promise<void>;
  onCreateDrill: (unitId: string, title: string) => Promise<void>;
  onReorderUnits: (orderedUnitIds: string[]) => void;
  onReorderDrills: (unitId: string, orderedDrillIds: string[]) => void;
  /**
   * feat-270 AT-25 — per-lesson approval state, or `null` when the tenant flag is
   * off (AT-29: the surface is ABSENT, not empty) or the state has not loaded.
   *
   * A LOOKUP rather than a field on `QuizUnit`, deliberately. Approval is a
   * SECOND AXIS (§10.1) computed by a different call from the one that builds the
   * tree, and merging it into the unit would invite exactly the collapse §10.1
   * forbids — `active`/`available_at` read live and are never gated, so
   * `On` + never-approved is a reachable and correct combination.
   *
   * Optional so every existing caller and test is unaffected.
   */
  approvalByLessonId?: ReadonlyMap<string, { state: LessonApprovalStatus; pendingCount: number }> | null;
}

/** Glyph for a Drill row: catalog icon_key → emoji, else free-form emoji, else default. */
function drillGlyph(drill: QuizLesson, glyphForKey: (k: string) => string): string {
  if (drill.icon_key) return glyphForKey(drill.icon_key);
  if (drill.emoji) return drill.emoji;
  return '📝';
}
/** Glyph for a Lesson row: catalog mascot_key → emoji, else default. */
function unitGlyph(unit: QuizUnit, glyphForKey: (k: string) => string): string {
  if (unit.mascot_key) return glyphForKey(unit.mascot_key);
  if (unit.emoji) return unit.emoji;
  return '📚';
}

/**
 * feat-187 (R2, D4) — the Lesson row's swatch: uploaded image → mascot → emoji →
 * 📚. Before this the tree ignored `image_s3_key` entirely while the meta panel
 * sitting next to it led with the image, so the two disagreed about what a
 * Lesson looks like. Same precedence as the panel swatch and the Flutter unit
 * card (feat-108 contract) now. Rows with no image render exactly as before.
 */
export const UnitSwatch: React.FC<{
  unit: QuizUnit;
  glyphForKey: (k: string) => string;
}> = ({ unit, glyphForKey }) => {
  const imageUrl = lessonImagePublicUrl(unit.image_s3_key);
  if (imageUrl) {
    return (
      <img
        src={imageUrl}
        alt=""
        data-testid={`unit-row-image-${unit.id}`}
        className="w-4 h-4 rounded object-cover shrink-0"
      />
    );
  }
  return <span className="text-base leading-none">{unitGlyph(unit, glyphForKey)}</span>;
};

/** Minimal inline "create" input shared by + Lesson / + Drill. */
const InlineCreate: React.FC<{
  placeholder: string;
  onCancel: () => void;
  onCommit: (title: string) => Promise<void>;
}> = ({ placeholder, onCancel, onCommit }) => {
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const commit = async () => {
    const title = value.trim();
    if (!title || busy) return;
    setBusy(true);
    try {
      await onCommit(title);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-center gap-1.5 px-2 py-1.5">
      <input
        autoFocus
        value={value}
        disabled={busy}
        placeholder={placeholder}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') onCancel();
        }}
        className="flex-1 min-w-0 text-sm bg-white border border-softBorder rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-coral/40"
      />
      <button
        type="button"
        onClick={commit}
        disabled={busy || !value.trim()}
        className="text-xs text-coral font-semibold px-1.5 py-1 rounded hover:bg-coral-light disabled:opacity-40"
      >
        Add
      </button>
      <button
        type="button"
        onClick={onCancel}
        className="text-xs text-inkMuted px-1 py-1 rounded hover:bg-gray-100"
      >
        ✕
      </button>
    </div>
  );
};

export const MasterDetailTree: React.FC<MasterDetailTreeProps> = ({
  units,
  search,
  onSearch,
  expandedUnitIds,
  onToggleUnit,
  selectedDrillId,
  onSelectDrill,
  glyphForKey,
  onCreateLesson,
  onCreateDrill,
  onReorderUnits,
  onReorderDrills,
  approvalByLessonId,
}) => {
  const [addingLesson, setAddingLesson] = useState(false);
  const [addingDrillFor, setAddingDrillFor] = useState<string | null>(null);
  // feat-231: the tree now scrolls inside a bounded pane, so a selection made
  // outside the visible window (first paint, a revert, a search that collapses)
  // would otherwise be off-screen. `block: 'nearest'` is load-bearing — it is a
  // NO-OP when the row is already visible, which is what keeps clicking a drill
  // from yanking the tree's scroll position out from under the author.
  const selectedRowRef = useRef<HTMLDivElement | null>(null);
  // drag bookkeeping: which row is being dragged within which list
  const [dragUnit, setDragUnit] = useState<string | null>(null);
  const [dragDrill, setDragDrill] = useState<{ unitId: string; drillId: string } | null>(null);

  // feat-361 NOTES §4 — a move keeps the SAME drill id, so keying on the id
  // alone never re-runs and the row, now at the end of another Lesson, can sit
  // off-screen. Re-run when the selected drill's parent Lesson changes too.
  const selectedDrillParentId = selectedDrillId
    ? units.find((u) => u.drills.some((d) => d.id === selectedDrillId))?.id ?? null
    : null;

  useEffect(() => {
    if (!selectedDrillId) return;
    // jsdom and some older browsers do not implement scrollIntoView — never
    // assume it exists.
    selectedRowRef.current?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }, [selectedDrillId, selectedDrillParentId]);

  const q = search.trim().toLowerCase();
  const matches = (s: string) => !q || s.toLowerCase().includes(q);

  // Filter: a Lesson shows if it or any Drill matches; a matching search auto-reveals Drills.
  const visibleUnits = units.filter(
    (u) => matches(u.title) || u.drills.some((l) => matches(l.title)),
  );

  const reorderUnit = (targetUnitId: string) => {
    if (!dragUnit || dragUnit === targetUnitId) return;
    const ids = units.map((u) => u.id);
    const from = ids.indexOf(dragUnit);
    const to = ids.indexOf(targetUnitId);
    if (from < 0 || to < 0) return;
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    onReorderUnits(ids);
  };

  const reorderDrill = (unitId: string, targetDrillId: string) => {
    if (!dragDrill || dragDrill.unitId !== unitId || dragDrill.drillId === targetDrillId) return;
    const unit = units.find((u) => u.id === unitId);
    if (!unit) return;
    const ids = unit.drills.map((l) => l.id);
    const from = ids.indexOf(dragDrill.drillId);
    const to = ids.indexOf(targetDrillId);
    if (from < 0 || to < 0) return;
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    onReorderDrills(unitId, ids);
  };

  return (
    // feat-231: `min-h-0` cancels the grid item's default content-based minimum
    // — without it this aside re-inflates the grid row to the full height of 45
    // lessons and the <nav> below never overflows, so it never scrolls.
    // `overflow-hidden` keeps the pinned search / + Lesson rows clipped to the
    // pane instead of spilling over the card's rounded corner.
    <aside className="border-r border-softBorder bg-cream/60 flex flex-col min-h-0 overflow-hidden">
      <div className="p-3 border-b border-softBorder shrink-0">
        <div className="relative">
          <input
            type="text"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder={`Search ${QUIZ_TERMS.lesson.plural} & ${QUIZ_TERMS.drill.plural}…`}
            className="w-full text-sm bg-white border border-softBorder rounded-full pl-9 pr-3 py-2 placeholder:text-inkMuted/70 focus:outline-none focus:ring-2 focus:ring-coral/40"
          />
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-inkMuted/60 text-sm">⌕</span>
        </div>
      </div>

      <nav className="flex-1 min-h-0 overflow-y-auto p-2 text-sm">
        {visibleUnits.map((unit) => {
          const expanded = expandedUnitIds.has(unit.id) || (q !== '' && unit.drills.some((l) => matches(l.title)));
          const drills = q ? unit.drills.filter((l) => matches(l.title) || matches(unit.title)) : unit.drills;
          return (
            <div key={unit.id} className="mt-0.5">
              {/* Lesson row */}
              <div
                draggable
                onDragStart={() => setDragUnit(unit.id)}
                onDragEnd={() => setDragUnit(null)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => reorderUnit(unit.id)}
                onClick={() => onToggleUnit(unit.id)}
                className={`treerow group flex items-center gap-1.5 rounded-lg px-2 py-2 hover:bg-gray-100 cursor-pointer ${
                  unit.active ? '' : 'opacity-60'
                }`}
              >
                <span className="drag opacity-0 group-hover:opacity-100 transition-opacity text-inkMuted/50 text-xs select-none cursor-grab">⠿</span>
                <span className="text-inkMuted text-xs w-3">{expanded ? '▾' : '▸'}</span>
                <UnitSwatch unit={unit} glyphForKey={glyphForKey} />
                <span className="flex-1 truncate font-semibold">{unit.title || '(untitled)'}</span>
                {/* feat-354 — Copy chip (design NOTES §2a): title → Copy → Skip drip →
                    approval chip → status pill; the status pill STAYS LAST. No `min-w`.
                    The original's title lives only in the tooltip / accessible name. */}
                {(() => {
                  const info = copyOfInfo(unit);
                  if (info.kind === 'original') return null;
                  const full = info.kind === 'copy' ? copyOfTitle(info.originalTitle) : COPY_OF_DELETED;
                  return (
                    <span
                      data-testid={`tree-row-copy-${unit.id}`}
                      title={full}
                      aria-label={full}
                      className="inline-flex items-center gap-0.5 bg-white border border-softBorder text-inkMuted text-[10px] font-bold rounded-full px-1.5 py-0.5 shrink-0"
                    >
                      <CopyGlyph className="w-2.5 h-2.5" />
                      {COPY_CHIP_LABEL}
                    </span>
                  );
                })()}
                {/* feat-256 — the third state, `Later`: Active but held back until
                    an instant that has not arrived. At 41 Lessons a queued Lesson
                    reading `On` is actively misleading.

                    `Later` is not a wording preference — it was chosen by
                    MEASUREMENT (notes §5.2). `min-w-[48px]` at the other two sites
                    is a FLOOR, not a cap, so a label wider than `Hidden` (45.55 /
                    48.72 / 45.89px across the three fonts on the stack) makes that
                    pill wider and the three states drift apart — which feat-238
                    AC13 forbids. `Later` is 36.22 / 38.88 / 36.45. The obvious word
                    is the wrong one: `Scheduled` is 17.6px over and `Queued` is
                    wider than `Hidden` in all three fonts. A date in the pill was
                    rejected on the same rule — variable width by construction. Do
                    not "improve" this wording.

                    THIS SITE HAS NO `min-w` AND MUST NOT GAIN ONE (notes §5.4).
                    The pill is the last child of the row, so nothing gets shoved
                    and the drift is harmless here; adding it would normalise a
                    pre-existing inconsistency, which is not this feature. A test
                    asserting a 48px width HERE fails correctly and must not be
                    "fixed" by changing this component.

                    The row's `opacity-60` above stays keyed on `!unit.active`, and
                    a `Later` Lesson IS active, so it must not dim. Dimming would
                    collapse "scheduled" back into "draft" — the exact confusion
                    this pill exists to remove.

                    The `title` (7.15) carries the precision `Later` omits, at zero
                    pixels of row width, which is the entire constraint. */}
                {/* feat-270 §10.2: the approval chip goes BEFORE the status
                    pill and gets NO `min-w`. feat-256's note below says this
                    site is deliberately the last child and "MUST NOT GAIN ONE";
                    that rule is about the STATUS pill, which is still last. */}
                {/* feat-303 — Skip drip marker (design NOTES §4): title → Skip drip →
                    approval chip → status pill, the status pill STAYS LAST. Outlined,
                    not filled, so it never reads as the pending chip. No `min-w`. */}
                {unit.drip_bypass && (
                  <span
                    data-testid={`tree-row-skip-drip-${unit.id}`}
                    className="bg-white border border-softBorder text-inkMuted text-[10px] font-bold rounded-full px-1.5 py-0.5 shrink-0"
                  >
                    {SKIP_DRIP_LABEL}
                  </span>
                )}
                {approvalByLessonId?.get(unit.id) && (
                  <LessonApprovalChip
                    lessonId={unit.id}
                    state={approvalByLessonId.get(unit.id)!.state}
                    pendingCount={approvalByLessonId.get(unit.id)!.pendingCount}
                  />
                )}
                {!unit.active ? (
                  <span
                    data-testid={`tree-row-status-${unit.id}`}
                    className="bg-gray-100 text-inkMuted text-[10px] font-bold rounded-full px-1.5 py-0.5"
                  >
                    Hidden
                  </span>
                ) : isScheduledLater(unit) ? (
                  <span
                    data-testid={`tree-row-status-${unit.id}`}
                    title={`Available ${formatReleaseInstant(unit.available_at)}`}
                    className="bg-preview-light text-preview text-[10px] font-bold rounded-full px-1.5 py-0.5"
                  >
                    Later
                  </span>
                ) : (
                  <span
                    data-testid={`tree-row-status-${unit.id}`}
                    className="bg-success-light text-success-on text-[10px] font-bold rounded-full px-1.5 py-0.5"
                  >
                    On
                  </span>
                )}
              </div>

              {/* Drills under an expanded Lesson */}
              {expanded && (
                <div className="ml-4 pl-2 border-l border-softBorder space-y-0.5 mt-0.5">
                  {drills.map((drill) => {
                    const selected = drill.id === selectedDrillId;
                    const itemCount = drill.question_items.length + drill.rehearsal_items.length;
                    return (
                      <div
                        key={drill.id}
                        ref={selected ? selectedRowRef : undefined}
                        draggable
                        onDragStart={(e) => {
                          e.stopPropagation();
                          setDragDrill({ unitId: unit.id, drillId: drill.id });
                        }}
                        onDragEnd={() => setDragDrill(null)}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => {
                          e.stopPropagation();
                          reorderDrill(unit.id, drill.id);
                        }}
                        onClick={() => onSelectDrill(unit.id, drill)}
                        className={`treerow group flex items-center gap-1.5 rounded-lg px-2 py-1.5 cursor-pointer ${
                          selected ? 'bg-coral-light text-coral' : 'text-ink hover:bg-gray-100'
                        } ${drill.active ? '' : 'opacity-60'}`}
                      >
                        <span className={`drag opacity-0 group-hover:opacity-100 transition-opacity text-xs select-none cursor-grab ${selected ? 'text-coral/40' : 'text-inkMuted/50'}`}>⠿</span>
                        <span className="w-5 h-5 rounded-md bg-white border border-softBorder grid place-items-center text-[11px] shrink-0">
                          {drillGlyph(drill, glyphForKey)}
                        </span>
                        <span className={`flex-1 truncate ${selected ? 'font-bold' : 'font-semibold'}`}>
                          {drill.title || '(untitled)'}
                        </span>
                        <span className="bg-gray-100 text-inkMuted text-[10px] font-bold rounded-full px-1.5 py-0.5">{itemCount}</span>
                        <span className="bg-gold-light text-gold text-[10px] font-bold rounded-full px-1.5 py-0.5">★{drill.xp_reward}</span>
                      </div>
                    );
                  })}

                  {addingDrillFor === unit.id ? (
                    <InlineCreate
                      placeholder={`New ${QUIZ_TERMS.drill.singular} title`}
                      onCancel={() => setAddingDrillFor(null)}
                      onCommit={async (title) => {
                        await onCreateDrill(unit.id, title);
                        setAddingDrillFor(null);
                      }}
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setAddingDrillFor(unit.id);
                      }}
                      className="w-full text-left text-coral text-xs font-semibold rounded-lg px-2 py-1.5 hover:bg-coral-light"
                    >
                      + {QUIZ_TERMS.drill.singular}
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {visibleUnits.length === 0 && q !== '' && (
          <div className="text-xs text-inkMuted px-2 py-4 text-center">No matches for “{search}”.</div>
        )}
      </nav>

      <div className="p-2 border-t border-softBorder shrink-0">
        {addingLesson ? (
          <InlineCreate
            placeholder={`New ${QUIZ_TERMS.lesson.singular} title`}
            onCancel={() => setAddingLesson(false)}
            onCommit={async (title) => {
              await onCreateLesson(title);
              setAddingLesson(false);
            }}
          />
        ) : (
          <button
            type="button"
            onClick={() => setAddingLesson(true)}
            className="w-full text-coral text-sm font-semibold rounded-lg px-2 py-2 hover:bg-coral-light text-left"
          >
            + {QUIZ_TERMS.lesson.singular}
          </button>
        )}
      </div>
    </aside>
  );
};

export default MasterDetailTree;
